import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import Header from '../components/Header';
import BackToDashboard from '../components/BackToDashboard';
import UsageDisplay from '../components/UsageDisplay';
import UpgradePrompt from '../components/Subscription/UpgradePrompt';

interface Lead {
  id: number;
  business_profile_id: number;
  status: string;
  notes: string | null;
  follow_up_date: string | null;
  created_at: string;
  business?: {
    name: string;
    address: string | null;
    phone: string | null;
    website: string | null;
    rating: number | null;
  };
}

const statusColumns = [
  { id: 'New', title: 'New', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { id: 'Contacted', title: 'Contacted', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  { id: 'Interested', title: 'Interested', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  { id: 'Closed', title: 'Closed', color: 'bg-slate-50 border-slate-200 text-slate-700' }
];

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeads();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchLeads depends on statusFilter
  }, [statusFilter]);

  const fetchLeads = async () => {
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const response = await api.get('/leads', { params });
      setLeads(response.data);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      console.log('Drag cancelled - no destination');
      return;
    }

    const { draggableId, destination, source } = result;
    
    // Don't update if dropped in the same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('Drag cancelled - same position');
      return;
    }

    const leadId = parseInt(draggableId);
    const newStatus = destination.droppableId;
    const oldStatus = source.droppableId;

    console.log('Drag end:', { leadId, oldStatus, newStatus });

    // Find the lead to verify it exists
    const lead = leads.find(l => l.id === leadId);
    if (!lead) {
      console.error('Lead not found:', leadId);
      return;
    }

    // Optimistically update the UI
    const updatedLeads = leads.map(lead => 
      lead.id === leadId ? { ...lead, status: newStatus } : lead
    );
    setLeads(updatedLeads);

    try {
      const response = await api.put(`/leads/${leadId}`, { status: newStatus });
      console.log('Status updated successfully:', response.data);
    } catch (err: any) {
      console.error('Error updating lead status:', err);
      // Revert on error
      setLeads(leads);
      alert(err.response?.data?.detail || 'Error updating lead status');
    }
  };

  const handleDeleteLead = async (leadId: number, leadName: string) => {
    if (!window.confirm(`Are you sure you want to remove "${leadName}" from your pipeline?`)) {
      return;
    }

    try {
      await api.delete(`/leads/${leadId}`);
      setLeads(leads.filter(lead => lead.id !== leadId));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error removing lead from pipeline');
    }
  };

  const handleMoveLead = async (leadId: number, direction: 'left' | 'right') => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    const currentStatus = lead.status;
    const currentIndex = statusColumns.findIndex(col => 
      col.id.toLowerCase() === currentStatus.toLowerCase()
    );

    if (currentIndex === -1) return;

    let newIndex: number;
    if (direction === 'left' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'right' && currentIndex < statusColumns.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return; // Can't move further in that direction
    }

    const newStatus = statusColumns[newIndex].id;

    // Optimistically update the UI
    const updatedLeads = leads.map(l => 
      l.id === leadId ? { ...l, status: newStatus } : l
    );
    setLeads(updatedLeads);

    try {
      await api.put(`/leads/${leadId}`, { status: newStatus });
    } catch (err: any) {
      // Revert on error
      setLeads(leads);
      alert(err.response?.data?.detail || 'Error moving lead');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const response = await api.get('/export/leads/csv', { 
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowExportMenu(false);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error exporting CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const response = await api.get('/export/leads/pdf', { 
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowExportMenu(false);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error exporting PDF');
    }
  };

  const getLeadsByStatus = (status: string) => {
    // Filter leads by status - handle case variations
    return leads.filter(lead => {
      const leadStatus = lead.status || '';
      return leadStatus.toLowerCase() === status.toLowerCase();
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header activeTab="pipeline" />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-12">
        <BackToDashboard />
        <UsageDisplay />
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-black text-slate-900 mb-2">Pipeline</h1>
            <p className="text-slate-600">Manage and track your leads</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition-all flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 z-10">
                  <button
                    onClick={handleExportCSV}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 rounded-t-xl transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export as CSV
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 rounded-b-xl transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Export as PDF
                  </button>
                </div>
              )}
            </div>
            <div className="custom-select">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 font-medium"
              >
                <option value="">All Statuses</option>
                {statusColumns.map(col => (
                  <option key={col.id} value={col.id}>{col.title}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
            <p className="mt-4 text-slate-600">Loading leads...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100">
              <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-3">No Leads Yet</h3>
            <p className="text-slate-500 mb-6">Start searching for businesses to add them to your pipeline.</p>
            <button
              onClick={() => navigate('/search')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-all"
            >
              Start Searching
            </button>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statusColumns.map((column) => (
                <div key={column.id} className="flex flex-col">
                  <div className={`${column.color} px-4 py-3 rounded-t-xl border-2 font-black text-sm uppercase tracking-widest mb-4`}>
                    {column.title} ({getLeadsByStatus(column.id).length})
                  </div>
                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 min-h-[200px] p-4 rounded-b-xl transition-colors ${
                          snapshot.isDraggingOver ? 'bg-slate-100' : 'bg-white border-2 border-slate-200'
                        }`}
                      >
                        {getLeadsByStatus(column.id).map((lead, index) => {
                          const currentColumnIndex = statusColumns.findIndex(col => col.id === column.id);
                          const canMoveLeft = currentColumnIndex > 0;
                          const canMoveRight = currentColumnIndex < statusColumns.length - 1;

                          return (
                            <Draggable key={lead.id} draggableId={lead.id.toString()} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`bg-white rounded-xl border border-slate-200 p-4 mb-3 shadow-sm hover:shadow-md transition-all ${
                                    snapshot.isDragging ? 'ring-2 ring-indigo-500' : ''
                                  }`}
                                >
                                  {/* Move buttons at the top */}
                                  <div className="flex items-center justify-between mb-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMoveLead(lead.id, 'left');
                                      }}
                                      disabled={!canMoveLeft}
                                      className={`p-1.5 rounded-lg transition-all ${
                                        canMoveLeft
                                          ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-indigo-600'
                                          : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                                      }`}
                                      title="Move left"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMoveLead(lead.id, 'right');
                                      }}
                                      disabled={!canMoveRight}
                                      className={`p-1.5 rounded-lg transition-all ${
                                        canMoveRight
                                          ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-indigo-600'
                                          : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                                      }`}
                                      title="Move right"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                      </svg>
                                    </button>
                                  </div>

                                  <div {...provided.dragHandleProps} className="cursor-move">
                                    <h4 className="font-black text-slate-900 mb-2 truncate">
                                      {lead.business?.name || `Lead #${lead.id}`}
                                    </h4>
                                  {lead.business?.address && (
                                    <p className="text-xs text-slate-500 mb-1 truncate">
                                      {lead.business.address}
                                    </p>
                                  )}
                                  {lead.business?.phone && (
                                    <p className="text-xs text-slate-500 mb-1">
                                      {lead.business.phone}
                                    </p>
                                  )}
                                  {lead.business?.website && (
                                    <a
                                      href={lead.business.website.startsWith('http') ? lead.business.website : `https://${lead.business.website}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-indigo-600 hover:underline truncate block"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {lead.business.website.replace(/^https?:\/\//, '')}
                                    </a>
                                  )}
                                  {lead.notes && (
                                    <p className="text-xs text-slate-600 mt-2 line-clamp-2">
                                      {lead.notes}
                                    </p>
                                  )}
                                  {lead.follow_up_date && (
                                    <div className="mt-2 text-xs text-amber-600 font-bold">
                                      Follow-up: {new Date(lead.follow_up_date).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-2 mt-3">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/business/${lead.business_profile_id}`);
                                    }}
                                    className="flex-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-all"
                                  >
                                    View Details
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteLead(lead.id, lead.business?.name || `Lead #${lead.id}`);
                                    }}
                                    className="px-3 py-1.5 bg-rose-100 text-rose-600 rounded-lg text-xs font-bold hover:bg-rose-200 transition-all flex items-center justify-center"
                                    title="Remove from pipeline"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        )}

        <UpgradePrompt
          message={upgradeMessage}
          show={showUpgrade}
          onClose={() => setShowUpgrade(false)}
        />
      </main>
    </div>
  );
};

export default Leads;
